/**
 * "What is waiting for me?" - the one question the portal's front page answers.
 *
 * Three kinds of thing ask something of a client: an invoice to pay, a quote to
 * answer and a contract to sign. This file gathers them from the three lists
 * the portal already serves and puts them in the order a person would deal with
 * them. React-free, so the order is pinned by the test beside it.
 *
 * ── NOTHING HERE DECIDES WHAT IS ACTIONABLE ────────────────────────────────
 *
 * Whether a quote can still be answered (`answerable`) and whether a contract
 * can be signed (`signable`) arrive from the server and are read as they came.
 * A browser deciding from a status and a date would offer a button the API then
 * refuses - the screens behind these rows already follow that rule, and a front
 * page that broke it would send people to a dead end from the first click.
 */
import type { PortalContract, PortalEstimate, PortalInvoice } from "../../../lib/portal";
import { isOverdue, isUnpaid, outstandingCents } from "../invoices/lib";

export type AttentionItem =
  | {
      kind: "invoice";
      id: string;
      number: string;
      title: string | null;
      overdue: boolean;
      dueDate: string | null;
      amountCents: number;
      currency: string;
    }
  | {
      kind: "estimate";
      id: string;
      number: string;
      title: string | null;
      validUntil: string | null;
      amountCents: number;
      currency: string;
    }
  | {
      kind: "contract";
      id: string;
      number: string | null;
      title: string;
      startDate: string | null;
      endDate: string | null;
    };

/** Sooner dates first; a row with no date goes last, it has no deadline to miss. */
function byDate(a: string | null, b: string | null): number {
  if (a === b) return 0;
  if (!a) return 1;
  if (!b) return -1;
  return a < b ? -1 : 1;
}

/**
 * Everything that waits for the reader, most pressing first.
 *
 * Money that is already late leads, then the rest of what is owed in the order
 * it falls due, then quotes by how soon they lapse, then contracts. A list a
 * module does not serve is simply absent (`undefined`), and contributes
 * nothing - it is not the same as an empty one, but it reads the same here.
 */
export function buildAttention(input: {
  invoices?: readonly PortalInvoice[];
  estimates?: readonly PortalEstimate[];
  contracts?: readonly PortalContract[];
}): AttentionItem[] {
  const invoices = (input.invoices ?? [])
    .filter(isUnpaid)
    .map((inv): AttentionItem & { kind: "invoice" } => ({
      kind: "invoice",
      id: inv.id,
      number: inv.number,
      title: inv.title,
      overdue: isOverdue(inv),
      dueDate: inv.dueDate,
      amountCents: outstandingCents(inv),
      currency: inv.currency,
    }))
    .sort((a, b) => Number(b.overdue) - Number(a.overdue) || byDate(a.dueDate, b.dueDate));

  const estimates = (input.estimates ?? [])
    .filter((est) => est.answerable)
    .map((est): AttentionItem & { kind: "estimate" } => ({
      kind: "estimate",
      id: est.id,
      number: est.number,
      title: est.title,
      validUntil: est.validUntil,
      amountCents: est.totalCents,
      currency: est.currency,
    }))
    .sort((a, b) => byDate(a.validUntil, b.validUntil));

  const contracts = (input.contracts ?? [])
    .filter((c) => c.signable)
    .map((c): AttentionItem => ({
      kind: "contract",
      id: c.id,
      number: c.number,
      title: c.title,
      startDate: c.startDate,
      endDate: c.endDate,
    }));

  return [...invoices, ...estimates, ...contracts];
}
